// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
// Wingo UI Pro · https://wingo-ui.com/legal/license

// haptic feedback for the web: one call, the right tick on every phone that has one.
// android (and chrome on touch laptops) vibrates through navigator.vibrate. safari on ios has no vibrate, but toggling a
// native <input type="checkbox" switch> plays the system tick (ios 17.4+), so the fallback clicks a hidden switch.
// everything is a silent no-op on the server, on desktops and before the page has seen a tap

export type HapticKind = "selection" | "light" | "medium" | "heavy" | "success" | "warning" | "error";

// vibration patterns in ms: short on purpose, a tick and never a buzz. selection is the detent of a wheel or a slider
export const HAPTIC_PATTERNS: Record<HapticKind, number | number[]> = {
  selection: 4,
  light: 8,
  medium: 14,
  heavy: 22,
  success: [10, 60, 16],
  warning: [16, 70, 16],
  error: [22, 50, 22, 50, 22],
};

// ios plays one fixed tick per switch toggle, so the notification kinds repeat it
const IOS_TICKS: Record<HapticKind, number> = {
  selection: 1,
  light: 1,
  medium: 1,
  heavy: 1,
  success: 2,
  warning: 2,
  error: 3,
};

const IOS_TICK_GAP = 70;

export type HapticsConfig = {
  // the app-wide switch (a "Vibrations" setting). default true
  enabled: boolean;
  // use the hidden-switch trick on ios safari. default true
  iosFallback: boolean;
};

let config: HapticsConfig = { enabled: true, iosFallback: true };

// app-wide settings, e.g. configureHaptics({ enabled: userSettings.vibrations }) once at startup
export function configureHaptics(options: Partial<HapticsConfig>) {
  config = { ...config, ...options };
}

export function getHapticsConfig(): HapticsConfig {
  return config;
}

function canVibrate() {
  return typeof navigator !== "undefined" && typeof navigator.vibrate === "function";
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  // ipados reports a mac user agent; touch points give it away
  return /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

// true when this device can play a haptic at all (vibrate, or the ios switch)
export function supportsHaptics() {
  if (typeof window === "undefined") return false;
  return canVibrate() || (config.iosFallback && isIOS());
}

// browsers block (and chrome logs) vibration before the first tap on the page
function hasUserActivation() {
  const activation = typeof navigator !== "undefined" ? navigator.userActivation : undefined;
  return activation ? activation.hasBeenActive : true;
}

let iosSwitch: HTMLLabelElement | null = null;

function iosTick() {
  if (!iosSwitch || !iosSwitch.isConnected) {
    const label = document.createElement("label");
    label.setAttribute("aria-hidden", "true");
    label.dataset.slot = "haptics-switch";
    label.style.cssText =
      "position:fixed;left:0;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;clip-path:inset(50%)";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.setAttribute("switch", "");
    input.tabIndex = -1;
    label.appendChild(input);
    // the click never reaches outside-click handlers of the page
    label.addEventListener("click", (event) => event.stopPropagation());
    document.body.appendChild(label);
    iosSwitch = label;
  }
  iosSwitch.click();
}

export type HapticOptions = {
  // fire even when configureHaptics({ enabled: false }) turned haptics off (a test button in settings)
  force?: boolean;
};

// plays a haptic and returns whether one was sent. call it from the handler of a real gesture (a tap, a drag
// crossing a threshold, a wheel landing on a new row), never from an effect on mount
export function haptic(kind: HapticKind = "light", { force = false }: HapticOptions = {}): boolean {
  if (typeof window === "undefined") return false;
  if (!config.enabled && !force) return false;
  if (!hasUserActivation()) return false;
  try {
    if (canVibrate()) return navigator.vibrate(HAPTIC_PATTERNS[kind] ?? HAPTIC_PATTERNS.light);
    if (config.iosFallback && isIOS()) {
      const ticks = IOS_TICKS[kind] ?? 1;
      iosTick();
      for (let i = 1; i < ticks; i++) setTimeout(iosTick, i * IOS_TICK_GAP);
      return true;
    }
  } catch {
    // vibrate throws in some embedded browsers; feedback is optional
  }
  return false;
}
